import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { CopyFormDialogData } from "../../../model/form.model";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { Task, TaskCopyError } from '../../../model/task.model';
import { TasksService } from "../../../services/tasks.service";
import { StoreService } from "../../../../../services/store.service";
import { filter, Subject, switchMap, takeUntil, tap } from "rxjs";
import { DialogService } from "../../../../../services/dialog.service";
import { MatSelect } from '@angular/material/select';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'copy-task-dialog',
  templateUrl: './copy-task-dialog.component.html',
  styleUrls: ['./copy-task-dialog.component.scss']
})
export class CopyTaskDialogComponent implements OnInit, OnDestroy {

  form: FormGroup<{
    taskId: FormControl<number[] | null>;
  }> = this.formBuilder.group({
    taskId: this.formBuilder.control<number[] | null>(null, Validators.required),
  });

  tasks: Task[];
  @ViewChild('multiSelect') private multiSelect: MatSelect;

  private readonly destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<CopyTaskDialogComponent>,
    private formBuilder: FormBuilder,
    private tasksService: TasksService,
    private store: StoreService,
    private dialogService: DialogService,
    private translateService: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: CopyFormDialogData,
  ) {
  }

  ngOnInit(): void {
    this.store.showLoader();
    this.tasksService.getSimilarTasks(this.data.task.id)
    .pipe(
      takeUntil(this.destroy$)
    )
    .subscribe((tasks) => {
      this.store.hideLoader();
      this.tasks = tasks;
      this.form.get('taskId')!.reset();
    })
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSubmit(): void {
    const { task } = this.data;
    if (this.form.valid) {
      this.dialogService.promptForCopyTask(task.name).afterClosed().pipe(
        filter((result) => !!result),
        tap(() => this.store.showLoader()),
        switchMap(() => this.tasksService.copyTasks(task.id, this.form.value.taskId!)),
        tap((response) => this.handleSuccess(response))
      ).subscribe();
    }
  }

  private handleSuccess(response: TaskCopyError[]): void {
    if(response.length){
      const taskCount = this.form.controls['taskId'].value?.length || 0;
      const content = "<strong>" + this.translateService.instant("TASK_COPIED", {counter: `${taskCount - response.length}/${taskCount}`}) + "</strong></br>"
            + response.map(error => `${error.taskName}: ${this.translateService.instant('TASK_COPY_ERROR.'+error.errorCode)}`).join('<br/>')
      this.dialogService.promptForCopyTaskResult(content)
    }
    else{
      this.store.showSuccessMsg('TASKS.COPY_SUCCESS');
    }

    this.tasksService.refreshTasks();
    this.dialogRef.close();
  }

  toggleSelectAll() {
      if (this.isAllSelected()) {
        this.form.controls['taskId'].setValue([]);
      } else {
        this.form.controls['taskId'].setValue([...this.tasks.map(task => task.id)]);
      }
  }

  isAllSelected(): boolean {
    return (this.form.controls['taskId'].value ?? []).length === (this.tasks ?? []).length;
  }
}
